import {
	Switch,
	Route,
	Redirect,
} from "react-router-dom";
import Homepage from './create/homepage/homepage';
import Info from './create/info/info';
import TemplatesMain from './create/templates/templates-main';
import Session from './create/session/session';
import Header from './header/header';
import EventsMain from './events/events-main';
import Analytics from './analytics/Analytics';
import PeopleMain from './people/people-main';
import ProductsMain from './products/products-main';
import ContentMain from './content/content-main';
import SettingsMain from './settings/settings-main';
import WhatsNewMain from './whats-new/whats-new-main';
import DocumentHead from './document-head';
import { EPermissions, NavMainItems, NavProfileItems } from '../../types/working-model';
import { useDispatch, useSelector } from "react-redux";
import { EventsState } from "../../store/types";
import { useEffect } from "react";
import { clearEvents, getEvents } from "../../store/actions/admin/events";
import usePrevious from "../../utils/use-previous";
import { userHasRoleOnActiveChannel } from "../../utils/utils";


// single source of truth for all routes
export const PATHNAMES = {
	Dashboard: "/admin",
	Events: "/admin/events",
	Analytics: "/admin/analytics/:tab?",
	AnalyticsLink: "/admin/analytics",
	People: "/admin/people",
	Content: "/admin/content",
	Products: "/admin/products",
	Settings: "/admin/settings/:tab?",
	SettingsLink: "/admin/settings",
	WhatsNew: "/admin/whats-new",
	Support: "https://help.brand.live/hc/en-us", //external link
	CreateEvent: "/admin/events/create",
	EventInfo: "/admin/events/create/info",
	EventBasePath: "/admin/events/create/:id",
	Event: {
		Landing: "/admin/events/create/:id/landing/:language?",
		LandingLink: (id: string, language?: string): string => `/admin/events/create/${id}/landing/${language || ""}`,
		Registration: "/admin/events/create/:id/registration/:language/:tab?",
		RegistrationLink: (id: string, language: string, tab?: string): string => `/admin/events/create/${id}/registration/${language}/${tab || ""}`,
		Availability: "/admin/events/create/:id/registration/:language/settings/availability",
		AvailabilityLink: (id: string, language: string): string => `/admin/events/create/${id}/registration/${language}/settings/availability`,
		Ticketing: "/admin/events/create/:id/registration/:language/settings/ticketing",
		TicketingLink: (id: string, language: string): string => `/admin/events/create/${id}/registration/${language}/settings/ticketing`,
		Event: "/admin/events/create/:id/event",
		EventLink: (id: string): string => `/admin/events/create/${id}/event`,
		// the following NavMainItems and NavProfileItems paths correspond to mapped menu items in the event panel
		HomePath: "/admin/events/create/:id/event/home",
		[NavMainItems.Home]: (id: string): string => `/admin/events/create/${id}/event/home`,
		SessionsPath: "/admin/events/create/:id/event/sessions/:tab?",
		[NavMainItems.Sessions]: (id: string, tab?: string): string => `/admin/events/create/${id}/event/sessions/${tab || ""}`,
		PeoplePath: "/admin/events/create/:id/event/people",
		[NavMainItems.People]: (id: string): string => `/admin/events/create/${id}/event/people`,
		// the remaining NavMainItems paths are not included in mvp so they are just placeholders to make typescript happy
		[NavMainItems.TellyClub]: (id: string): string => `/admin/events/create/${id}/event`,
		[NavMainItems.Notifications]: (id: string): string => `/admin/events/create/${id}/event`,
		[NavMainItems.Catalog]: (id: string): string => `/admin/events/create/${id}/event`,
		TermsConditionsPath: "/admin/events/create/:id/event/terms-conditions",
		[NavProfileItems.TermsAndConditions]: (id: string): string => `/admin/events/create/${id}/event/terms-conditions`,
		PrivacyPolicyPath: "/admin/events/create/:id/event/privacy-policy",
		[NavProfileItems.PrivacyPolicy]: (id: string): string => `/admin/events/create/${id}/event/privacy-policy`,
		// the remaining NavProfileItem path is not included in mvp so it is just a placeholder to make typescript happy
		[NavProfileItems.Profile]: (id: string): string => `/admin/events/create/${id}/event`,
		// conditional routing for theme so the panel can change but the editor view remains
		Theme: "/admin/events/create/:id/event/theme",
		ThemeLanding: "/admin/events/create/:id/event/theme/landing",
		ThemeSession: "/admin/events/create/:id/event/theme/session/:session/:language",
		ThemeLink: (id: string, editor?: string): string => `/admin/events/create/${id}/event/theme/${editor || ""}`,
	},
	SessionBasePath: "/admin/events/create/:id/session/:session/:language?",
	Session: {
		Session: "/admin/events/create/:id/session/:session/:language/:tab?",
		SessionLink: (id: string, sessionId: string, language: string, tab?: string): string => `/admin/events/create/${id}/session/${sessionId}/${language}/${tab || ""}`,

	}
};

//Must use all the paths, not just the path base in order to get access to the params
//Getting only the string path names from the PATHNAME map
const eventPaths = Object.values(PATHNAMES.Event).filter(value => typeof value === "string") as string[];

const Admin: React.FC = () => {
	const {
		AuthReducer: { user, token, changingChannels }
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();
	const lastChannel = usePrevious(user?.active_channel);

	useEffect(() => {
		if (user?.active_channel !== lastChannel) {
			dispatch(clearEvents());
			dispatch(getEvents(user!.active_channel, token!));
		}
	}, [user?.active_channel, token, dispatch, changingChannels, lastChannel, user]);

	// <Header /> must be included in each Route so that it has access to useParams()
	return (
		<div className="side-nav">
			<DocumentHead />
			<Switch>
				<Route path={[PATHNAMES.SessionBasePath, PATHNAMES.Event.ThemeSession]} >
					<Session />
					<Header />
				</Route>
				<Route path={PATHNAMES.EventInfo}>
					<Info />
					<Header />
				</Route>

				{/* event base path must be after info page so router doesn't think info is :id */}
				<Route path={eventPaths}>
					<Homepage />
					<Header />
				</Route>
				<Route path={PATHNAMES.CreateEvent}>
					<TemplatesMain />
					<Header />
				</Route>
				<Route path={PATHNAMES.Events}>
					<EventsMain />
					<Header />
				</Route>
				<Route path={PATHNAMES.Analytics}>
					<Analytics />
					<Header />
				</Route>
				<Route path={PATHNAMES.People}>
					<PeopleMain />
					<Header />
				</Route>
				<Route path={PATHNAMES.Products}>
					<ProductsMain />
					<Header />
				</Route>
				<Route path={PATHNAMES.Content}>
					<ContentMain />
					<Header />
				</Route>
				<Route path={PATHNAMES.Settings}>
					<SettingsMain />
					<Header />
				</Route>
				<Route path={PATHNAMES.WhatsNew}>
					<WhatsNewMain />
					<Header />
				</Route>
				<Route path={PATHNAMES.Dashboard}>
					{userHasRoleOnActiveChannel([EPermissions.Owner, EPermissions.Admin, EPermissions.Moderation], user!) &&
						<Redirect to={PATHNAMES.Events} />
					}
					{userHasRoleOnActiveChannel([EPermissions.Analytics], user!) &&
						<Redirect to={PATHNAMES.AnalyticsLink} />
					}
					<Header />
				</Route>
			</Switch>
		</div>
	);
};

export default Admin;